import { first } from 'lodash';

import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  EntityKind,
  HypotheticalInsuranceEndsOnKind,
  HypotheticalInsuranceTransferFrequency,
  HypotheticalInsuranceTransferTaxKind,
} from '@/types/schema';

import {
  defaultPolicy as insurancePolicySubformDefaultPolicy,
  defaultValues as insurancePolicySubformDefaultValues,
} from '../../InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import {
  HypotheticalILITFormModalShape,
  HypotheticalILITInsurancePolicyType,
} from './HypotheticalILITModal.types';

// intentionally omit the id from the default values so that a consumer has to read it
export const HYPOTHETICAL_ILIT_MODAL_DEFAULT_VALUES: Omit<
  HypotheticalILITFormModalShape,
  'id'
> = {
  transferName: 'Hypothetical ILIT funding',
  destinationEntityId: '',
  destinationEntityType: EntityKind.IlitTrust,
  destinationEntityName: 'Hypothetical ILIT',
  destinationEntityGrantor: '',
  existingInsurancePolicyId: '',
  createdInsurancePolicyId: null,
  endsOnKind: HypotheticalInsuranceEndsOnKind.PolicyHolderDeath,
  transferFromId: '',
  transferFrequency: HypotheticalInsuranceTransferFrequency.OneTime,
  transferTaxKind:
    HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers,
  transferValue: null,
  _createANewHypotheticalTrust: true,
  _hypotheticalInsurancePolicyType:
    HypotheticalILITInsurancePolicyType.Existing,
  insurancePolicyDetailsSubform: {
    ...insurancePolicySubformDefaultValues,
    policies: [insurancePolicySubformDefaultPolicy],
  },
};

export function useHypotheticalILITModalDefaultValues(): Omit<
  HypotheticalILITFormModalShape,
  'id'
> {
  const { primaryClients } = useHouseholdDetailsContext();
  const firstClient = first(primaryClients);

  const destinationEntityGrantor =
    firstClient?.id ??
    HYPOTHETICAL_ILIT_MODAL_DEFAULT_VALUES.destinationEntityGrantor;

  return {
    ...HYPOTHETICAL_ILIT_MODAL_DEFAULT_VALUES,
    destinationEntityGrantor,
  };
}

/**
 * A list of EntityKinds that can have insurance policies attached.
 *
 * This form will create a draft entity of one of these types.
 *
 * If updating this list, also update the following fragments:
 * - HypotheticalILITModalData_InsurancePolicy
 */
export const HYPOTHETICAL_INSURANCE_ENTITY_KIND_OPTIONS: EntityKind[] = [
  EntityKind.CltTrust,
  EntityKind.CrtTrust,
  EntityKind.DonorAdvisedFund,
  EntityKind.IlitTrust,
  EntityKind.InsurancePersonalAccount,
  EntityKind.IrrevocableTrust,
  EntityKind.PrivateFoundation,
  EntityKind.RevocableTrust,
  EntityKind.SlatTrust,
];
